/**
 * The Disposal page's words and its pure rules (unit-tested): module names, status labels, the
 * approve body, and what an API refusal means.
 */
import { ApiError } from '@lodex-pro/web-shared';
import type {
  ApproveDisposalBody,
  DisposalLicenseMatchStatus,
  DisposalModuleKey,
  DisposalModules,
  DisposalPortalStatus,
  DisposalRegistrationStatus,
} from './hooks';

/**
 * The 10 §49 modules (shared DISPOSAL_MODULE_KEYS), in the order the API lists them, with the
 * names Paelode staff use. Mirrored, not imported: web-admin takes `type` imports only from
 * the shared contracts. A test pins the key set equal.
 */
export const DISPOSAL_MODULES: ReadonlyArray<{ key: DisposalModuleKey; label: string }> = [
  { key: 'moduleTicketView', label: 'Ticket view' },
  { key: 'moduleDailyEmail', label: 'Daily email' },
  { key: 'moduleCustomReports', label: 'Custom reports' },
  { key: 'moduleVolumeTrends', label: 'Volume trends' },
  { key: 'moduleCaptureRate', label: 'Capture rate' },
  { key: 'moduleDiversion', label: 'Diversion' },
  { key: 'moduleAcceptanceBySite', label: 'Acceptance by site' },
  { key: 'moduleForwardForecast', label: 'Forward forecast' },
  { key: 'moduleDataExport', label: 'Data export' },
  { key: 'moduleMqttPublish', label: 'MQTT publish' },
];

/** How many of the 10 modules are on. */
export function modulesOn(m: DisposalModules): number {
  return DISPOSAL_MODULES.filter((d) => m[d.key]).length;
}

export const PORTAL_STATUS_LABELS: Readonly<Record<DisposalPortalStatus, string>> = {
  PAYMENT_PENDING: 'Payment pending',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
};

export function portalStatusTone(
  s: DisposalPortalStatus,
): 'positive' | 'warning' | 'danger' | 'neutral' {
  if (s === 'ACTIVE') return 'positive';
  if (s === 'PAYMENT_PENDING') return 'warning';
  return 'danger';
}

export const REGISTRATION_STATUS_LABELS: Readonly<Record<DisposalRegistrationStatus, string>> = {
  PENDING_EMAIL_VERIFY: 'Email not verified',
  EMAIL_VERIFIED: 'Awaiting review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const LICENSE_MATCH_LABELS: Readonly<Record<DisposalLicenseMatchStatus, string>> = {
  MATCHED: 'License matched',
  UNMATCHED: 'License not matched',
  NOT_PROVIDED: 'No license given',
  MANUALLY_VERIFIED: 'Manually verified',
};

/**
 * The API requires a verification note (10+ characters) to approve a registration whose
 * license did not match a known facility (approve.ts,
 * DISPOSAL_REGISTRATION_MANUAL_VERIFICATION_NOTE_REQUIRED).
 */
export function needsVerificationNote(s: DisposalLicenseMatchStatus): boolean {
  return s === 'UNMATCHED' || s === 'NOT_PROVIDED';
}

export const VERIFICATION_NOTE_MIN = 10;
export const REJECT_REASON_MIN = 10;
/** Suspend / reactivate reason, once trimmed (the API and the DB check the same, 0234). */
export const STATUS_REASON_MIN = 10;
export const STATUS_REASON_MAX = 500;

/** True when a suspend / reactivate reason is long enough, and not too long, once trimmed. */
export function statusReasonOk(reason: string): boolean {
  const n = reason.trim().length;
  return n >= STATUS_REASON_MIN && n <= STATUS_REASON_MAX;
}

export interface ApproveDraft {
  readonly mode: 'existing' | 'new';
  readonly disposalCompanyId: string;
  readonly name: string;
  readonly slug: string;
  readonly legalName: string;
  readonly note: string;
}

/**
 * The approve body for a draft: exactly one of `disposalCompanyId` or
 * `createNewDisposalCompany` (the API's XOR rule), optional fields only when given, the note only
 * when typed. Null when the draft cannot be sent yet.
 */
export function approveBody(
  d: ApproveDraft,
  licenseMatch: DisposalLicenseMatchStatus,
): ApproveDisposalBody | null {
  const note = d.note.trim();
  if (needsVerificationNote(licenseMatch) && note.length < VERIFICATION_NOTE_MIN) return null;
  if (note !== '' && note.length < VERIFICATION_NOTE_MIN) return null;
  const noteField = note === '' ? {} : { manualVerificationNote: note };
  if (d.mode === 'existing') {
    if (d.disposalCompanyId === '') return null;
    return { disposalCompanyId: d.disposalCompanyId, ...noteField };
  }
  const name = d.name.trim();
  if (name.length < 2) return null;
  const slug = d.slug.trim();
  const legalName = d.legalName.trim();
  return {
    createNewDisposalCompany: {
      name,
      ...(slug === '' ? {} : { slug }),
      ...(legalName === '' ? {} : { legalName }),
    },
    ...noteField,
  };
}

/**
 * The API's refusals, in the words of its error catalog (shared errors/catalog.ts). ApiError's
 * `message` is "API <status>: <path>", not for people, so the code decides the line.
 */
const REFUSAL_COPY: Readonly<Record<string, string>> = {
  DISPOSAL_COMPANY_ALREADY_ACTIVE: 'This disposal company is already active.',
  DISPOSAL_COMPANY_NOT_ACTIVATABLE:
    'This disposal company cannot be activated from its current status.',
  DISPOSAL_COMPANY_NOT_ACTIVE: 'Activate this disposal company before switching modules.',
  DISPOSAL_COMPANY_NOT_FOUND: 'That disposal company was not found.',
  DISPOSAL_COMPANY_ALREADY_SUSPENDED: 'This disposal company is already suspended.',
  DISPOSAL_COMPANY_NOT_SUSPENDABLE: 'Only an active disposal company can be suspended.',
  DISPOSAL_COMPANY_NOT_SUSPENDED: 'This disposal company is not suspended.',
  DISPOSAL_MODULE_UNKNOWN: 'That module is not one of the ten.',
  DISPOSAL_REGISTRATION_NOT_FOUND: 'That registration was not found.',
  DISPOSAL_REGISTRATION_ALREADY_PROCESSED: 'That registration has already been processed.',
  DISPOSAL_REGISTRATION_NOT_EMAIL_VERIFIED: 'The applicant has not verified their email yet.',
  DISPOSAL_REGISTRATION_MANUAL_VERIFICATION_NOTE_REQUIRED:
    'The license did not match a known facility: add a verification note (10+ characters).',
  DISPOSAL_TENANT_ALREADY_EXISTS: 'A disposal company with that slug already exists.',
  COGNITO_DISPOSAL_USER_CREATION_FAILED:
    'The applicant’s sign-in could not be created. Try again in a moment.',
  VALIDATION_FAILED: 'Check the fields and try again.',
};

/** The line for a refused disposal action. */
export function disposalErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code !== null && REFUSAL_COPY[err.code] !== undefined) {
    return REFUSAL_COPY[err.code] ?? '';
  }
  return 'Something went wrong. Try again.';
}
